import { useState, useEffect, useCallback, useMemo } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { Channel, ChannelItem, ChannelCollaborator } from 'types/channel';
import {
  getChannel,
  getChannelItems,
  getChannelCollaborators,
  GetChannelResponse,
  GetChannelItemsResponse,
  GetCollaboratorsResponse,
} from '../api/marketplaceApi';
import { usePlatformAdmin } from './usePlatformAdmin';

export type ChannelPermissions = {
  canEditDetails: boolean;
  canManageContent: boolean;
  canPublish: boolean;
  canDelete: boolean;
  canManageCollaborators: boolean;
  isPlatformAdmin: boolean;
};

export interface UseChannelManageResult {
  channel: Channel | null;
  items: ChannelItem[];
  collaborators: ChannelCollaborator[];
  permissions: ChannelPermissions;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

type ChannelBasePermissions = Omit<ChannelPermissions, 'isPlatformAdmin'>;

const NO_BASE_PERMISSIONS: ChannelBasePermissions = {
  canEditDetails: false,
  canManageContent: false,
  canPublish: false,
  canDelete: false,
  canManageCollaborators: false,
};

function derivePermissions(
  channel: Channel,
  collaborators: ChannelCollaborator[],
  userId: string | undefined,
  organizationId: string | undefined
): ChannelBasePermissions {
  if (!userId || !organizationId) {
    return NO_BASE_PERMISSIONS;
  }

  const isOwner =
    channel.created_by === userId ||
    channel.owner_tenant_id === organizationId;
  const collaborator = collaborators.find((c) => c.identity_id === userId);
  const isEditor = collaborator?.role === 'editor';

  return {
    canEditDetails: isOwner || isEditor,
    canManageContent: isOwner || isEditor || !!collaborator,
    canPublish: isOwner || isEditor,
    canDelete: isOwner,
    canManageCollaborators: isOwner,
  };
}

export function useChannelManage(channelId: string | null): UseChannelManageResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;
  const userId = impersonationDetails?.targetUserId;
  const { isPlatformAdmin } = usePlatformAdmin();

  const [channel, setChannel] = useState<Channel | null>(null);
  const [items, setItems] = useState<ChannelItem[]>([]);
  const [collaborators, setCollaborators] = useState<ChannelCollaborator[]>([]);
  const [basePermissions, setBasePermissions] =
    useState<ChannelBasePermissions>(NO_BASE_PERMISSIONS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const permissions = useMemo<ChannelPermissions>(
    () => ({ ...basePermissions, isPlatformAdmin }),
    [basePermissions, isPlatformAdmin]
  );

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!apiClients || !organizationId || !channelId) {
      setChannel(null);
      setItems([]);
      setCollaborators([]);
      setBasePermissions(NO_BASE_PERMISSIONS);
      return;
    }

    let cancelled = false;

    const fetchAll = async () => {
      setLoading(true);
      setError(null);

      try {
        const [channelRes, itemsRes, collabRes]: [
          GetChannelResponse,
          GetChannelItemsResponse,
          GetCollaboratorsResponse,
        ] = await Promise.all([
          getChannel(apiClients.schemaClient, channelId),
          getChannelItems(apiClients.schemaClient, channelId),
          getChannelCollaborators(apiClients.schemaClient, channelId),
        ]);

        if (!cancelled) {
          setChannel(channelRes.channel);
          setItems(itemsRes.data);
          setCollaborators(collabRes.collaborators);
          setBasePermissions(
            derivePermissions(channelRes.channel, collabRes.collaborators, userId, organizationId)
          );
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load channel';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId, userId, channelId, fetchTrigger]);

  return { channel, items, collaborators, permissions, loading, error, refetch };
}
